// The recycler's collection network: suppliers, collection points, labels
// (zones), facilities, routes, supplier agreements and completion flows.

import type { CollectionFlow, CollectionFlowStep, FlowContract, FlowStepInput, FlowStepType, FlowType, ResolvedFlow } from '../../types/collection-flow.type';
import type { Facility, FacilityType } from '../../types/facility.type';
import type { Route, RouteStop } from '../../types/route.type';
import type { SupplierAgreementDocument, SupplierAgreementPrice } from '../../types/supplier-agreement.type';
import type { Supplier, SupplierCollectionPoint, SupplierSource, ZoneSummary } from '../../types/supplier.type';
import type { Zone } from '../../types/zone.type';
import {
  DEMO_COLLECTION_POINTS,
  DEMO_CONTAINERS,
  DEMO_FACILITIES,
  DEMO_MATERIAL_IDS,
  DEMO_OUTLET_ORG_ID,
  DEMO_RECYCLER_MANAGERS,
  DEMO_RECYCLER_ORG_ID,
  DEMO_ROUTES,
  DEMO_SITES,
  DEMO_SUPPLIERS,
  DEMO_USER_ID,
  DEMO_ZONES,
  MATERIAL_PRICE_SAR_PER_KG,
  OUTLET_BASKET_MATERIAL_IDS,
  daysAgo,
  ids,
  iso,
  isoDate,
  materialById,
  polylineKm,
} from '../core';
import { makeCounter, mapsUrl, nowIso, round2, tr } from './common';
import { truckRecordById } from './fleet';
import { organizationName } from './people';
import { generatedPdfUrl, uploadedFileUrl } from './uploads';

const M = DEMO_MATERIAL_IDS;
const R = DEMO_RECYCLER_ORG_ID;

// ── Suppliers ──────────────────────────────────────────────────────────────

type StoredSupplier = Omit<Supplier, 'collection_points_count' | 'collection_points' | 'linked_organization_name'> & { name_ar?: string };

const SUPPLIER_MATERIALS: string[][] = [
  OUTLET_BASKET_MATERIAL_IDS,
  [M.occ, M.pet, M.aluminium_cans, M.uco, M.clear_glass],
  [M.occ, M.office_paper, M.hdpe, M.pp],
  [M.occ, M.ldpe_film, M.pet, M.mixed_paper, M.aluminium_cans],
  [M.mixed_paper, M.office_paper, M.newspaper, M.pet, M.occ],
];

const SUPPLIER_NOTES = [
  'Linked Recyclee outlet: sites sync automatically. Priority account, weekly volumes agreed.',
  'Hotel engineering team handles staging; cardboard is baled on site.',
  'Non-clinical recyclables only. Collection crews must carry KFMC contractor badges.',
  'Mall service yard closes to trucks 10:00-22:00 Thursday and Friday.',
  'School calendar: volumes drop sharply during summer and mid-year breaks.',
];

/** Price factor vs the catalog price, per supplier (the outlet's agreement is the catalog price). */
const PRICE_FACTOR = [1, 0.95, 0.9, 1, 0.92];

let suppliers: StoredSupplier[] = [];
const supplierCounter = makeCounter(100);

function supplierRecord(i: number): StoredSupplier {
  const s = DEMO_SUPPLIERS[i];
  return {
    id: s.id,
    owner_org_id: R,
    supplier_org_id: s.linked_organization_id,
    name: s.name_en,
    name_ar: s.name_ar,
    contacts: [
      { name: s.contact_name, phone: s.contact_phone, email: s.contact_email, role: i === 0 ? 'Sustainability Manager' : 'Operations contact' },
      ...(i === 0 ? [{ name: 'Faisal Al-Qahtani', phone: '+966500010101', email: 'f.alqahtani@alnakheel.example', role: 'Head of Operations' }] : []),
    ],
    material_ids: [...SUPPLIER_MATERIALS[i]],
    status: 'active',
    internal_notes: SUPPLIER_NOTES[i],
    source: (i === 0 ? 'org_linked' : 'created') as SupplierSource,
    created_by_user_id: DEMO_USER_ID,
    created_at: iso(daysAgo(470 - i * 52)),
    updated_at: iso(daysAgo(10 + i * 4)),
  };
}

export function supplierRecordById(id: string | null | undefined): StoredSupplier | undefined {
  return suppliers.find((s) => s.id === id);
}

export function supplierDisplayName(id: string | null | undefined): string | null {
  const s = supplierRecordById(id);
  return s ? tr(s.name, s.name_ar) : null;
}

export function serializeSupplier(s: StoredSupplier, withPoints: boolean): Supplier {
  const points = collectionPoints.filter((p) => p.supplier_id === s.id);
  const { name_ar: _nameAr, ...rest } = s;
  void _nameAr;
  return {
    ...rest,
    name: tr(s.name, s.name_ar),
    linked_organization_name: organizationName(s.supplier_org_id),
    collection_points_count: points.filter((p) => p.status !== 'archived').length,
    ...(withPoints ? { collection_points: points.map(serializePoint) } : {}),
  };
}

export function listSupplierRecords(): StoredSupplier[] {
  return suppliers.filter((s) => s.status !== 'archived');
}

export function createSupplier(input: Record<string, unknown>): StoredSupplier {
  const n = supplierCounter.next();
  const supplier: StoredSupplier = {
    id: ids.supplier(n),
    owner_org_id: R,
    supplier_org_id: (input.supplier_org_id as string | null | undefined) ?? null,
    name: String(input.name ?? 'New supplier'),
    contacts: Array.isArray(input.contacts) ? (input.contacts as Supplier['contacts']) : [],
    material_ids: Array.isArray(input.material_ids) ? (input.material_ids as string[]) : [],
    status: (input.status as Supplier['status']) ?? 'active',
    internal_notes: (input.internal_notes as string | null | undefined) ?? null,
    source: input.supplier_org_id ? 'org_linked' : 'created',
    created_by_user_id: DEMO_USER_ID,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  suppliers.unshift(supplier);
  return supplier;
}

export function updateSupplier(id: string, patch: Record<string, unknown>): StoredSupplier | undefined {
  const s = supplierRecordById(id);
  if (!s) return undefined;
  if (typeof patch.name === 'string') {
    s.name = patch.name;
    s.name_ar = patch.name;
  }
  if (Array.isArray(patch.contacts)) s.contacts = patch.contacts as Supplier['contacts'];
  if (Array.isArray(patch.material_ids)) s.material_ids = patch.material_ids as string[];
  if (typeof patch.status === 'string') s.status = patch.status as Supplier['status'];
  if (patch.internal_notes !== undefined) s.internal_notes = (patch.internal_notes as string | null) ?? null;
  if (patch.supplier_org_id !== undefined) {
    s.supplier_org_id = (patch.supplier_org_id as string | null) ?? null;
    if (s.supplier_org_id && s.source === 'created') s.source = 'org_linked';
  }
  s.updated_at = nowIso();
  return s;
}

export function archiveSupplier(id: string): boolean {
  const s = supplierRecordById(id);
  if (!s) return false;
  s.status = 'archived';
  s.updated_at = nowIso();
  collectionPoints.forEach((p) => {
    if (p.supplier_id === id) p.status = 'archived';
  });
  return true;
}

// ── Labels (zones) ─────────────────────────────────────────────────────────

let zones: Zone[] = [];
const zoneCounter = makeCounter(100);
const zoneNamesAr = new Map<string, string>();

function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i, i += 1) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const crosses = yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi || 1e-12) + xi;
    if (crosses) inside = !inside;
  }
  return inside;
}

function zoneName(z: Zone): string {
  return tr(z.name, zoneNamesAr.get(z.id));
}

export function labelsFor(lat: number, lng: number): ZoneSummary[] {
  return zones
    .filter((z) => z.status !== 'archived' && z.boundary.length >= 3 && pointInRing(lng, lat, z.boundary))
    .map((z) => ({ id: z.id, name: zoneName(z), color: z.color }));
}

export function serializeZone(z: Zone): Zone {
  return {
    ...z,
    name: zoneName(z),
    collection_points_count: collectionPoints.filter(
      (p) => p.status !== 'archived' && pointInRing(p.longitude, p.latitude, z.boundary),
    ).length,
  };
}

export function listZoneRecords(): Zone[] {
  return zones.filter((z) => z.status !== 'archived');
}

export function zoneRecordById(id: string): Zone | undefined {
  return zones.find((z) => z.id === id);
}

export function createZone(input: Record<string, unknown>): Zone {
  const n = zoneCounter.next();
  const zone: Zone = {
    id: ids.zone(n),
    owner_org_id: R,
    name: String(input.name ?? 'New label'),
    color: String(input.color ?? '#7C3AED'),
    boundary: Array.isArray(input.boundary) ? (input.boundary as number[][]) : [],
    status: (input.status as Zone['status']) ?? 'active',
    collection_points_count: 0,
    created_by_user_id: DEMO_USER_ID,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  zones.push(zone);
  return zone;
}

export function updateZone(id: string, patch: Record<string, unknown>): Zone | undefined {
  const z = zoneRecordById(id);
  if (!z) return undefined;
  if (typeof patch.name === 'string') {
    z.name = patch.name;
    zoneNamesAr.set(z.id, patch.name);
  }
  if (typeof patch.color === 'string') z.color = patch.color;
  if (Array.isArray(patch.boundary)) z.boundary = patch.boundary as number[][];
  if (typeof patch.status === 'string') z.status = patch.status as Zone['status'];
  z.updated_at = nowIso();
  return z;
}

export function archiveZone(id: string): boolean {
  const z = zoneRecordById(id);
  if (!z) return false;
  z.status = 'archived';
  z.updated_at = nowIso();
  return true;
}

// ── Collection points ──────────────────────────────────────────────────────

type StoredPoint = SupplierCollectionPoint & { name_ar?: string; collection_site_id?: string | null };

let collectionPoints: StoredPoint[] = [];
const pointCounter = makeCounter(100);

const POINT_MATERIALS: Record<number, string[]> = {
  7: [M.occ, M.pet, M.aluminium_cans, M.uco, M.clear_glass],
  8: [M.occ, M.office_paper, M.pp],
  9: [M.occ, M.hdpe, M.pp],
  10: [M.occ, M.ldpe_film, M.pet, M.mixed_paper, M.aluminium_cans],
  11: [M.mixed_paper, M.newspaper, M.pet, M.occ],
  12: [M.office_paper, M.mixed_paper, M.pet],
};

const ALL_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const SCHOOL_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];

function siteMaterials(siteId: string): string[] {
  return Array.from(new Set(DEMO_CONTAINERS.filter((c) => c.site_id === siteId).map((c) => c.material_id)));
}

function pointRecord(i: number): StoredPoint {
  const p = DEMO_COLLECTION_POINTS[i];
  const supplier = DEMO_SUPPLIERS.find((s) => s.id === p.supplier_id)!;
  const site = p.site_id ? DEMO_SITES.find((s) => s.id === p.site_id) : undefined;
  const n = i + 1;
  const [start, end] = site
    ? site.opening_hours === '24 hours'
      ? ['00:00', '23:59']
      : site.opening_hours.split('-').map((x) => (x === '24:00' ? '23:59' : x))
    : n >= 11
      ? ['06:30', '14:00']
      : ['06:00', '22:00'];
  return {
    id: p.id,
    supplier_id: p.supplier_id,
    owner_org_id: R,
    name: p.name_en,
    name_ar: p.name_ar,
    address: p.address,
    city: p.city,
    latitude: p.lat,
    longitude: p.lng,
    google_maps_url: mapsUrl(p.lat, p.lng),
    status: 'active',
    contact_name: site ? site.manager_name : supplier.contact_name,
    contact_phone: site ? site.phone : supplier.contact_phone,
    contact_email: site ? `${site.code.toLowerCase()}@alnakheel.example` : supplier.contact_email,
    material_ids: site ? siteMaterials(site.id) : POINT_MATERIALS[n] ?? [M.occ],
    operating_hours: { days: n >= 11 ? SCHOOL_WEEK : ALL_WEEK, start_time: start, end_time: end },
    notes: site ? `Synced from ${supplier.name_en} collection site ${site.code}.` : null,
    collection_site_id: site?.id ?? null,
    created_at: iso(daysAgo(450 - i * 21)),
    updated_at: iso(daysAgo(8 + i)),
  };
}

export function serializePoint(p: StoredPoint): SupplierCollectionPoint {
  const { name_ar: _nameAr, ...rest } = p;
  void _nameAr;
  return { ...rest, name: tr(p.name, p.name_ar), labels: labelsFor(p.latitude, p.longitude) };
}

export function listPointRecords(): StoredPoint[] {
  return collectionPoints;
}

export function pointRecordById(id: string | null | undefined): StoredPoint | undefined {
  return collectionPoints.find((p) => p.id === id);
}

export function pointDisplayName(id: string | null | undefined): string | null {
  const p = pointRecordById(id);
  return p ? tr(p.name, p.name_ar) : null;
}

/** The synced collection point that mirrors an outlet collection site. */
export function pointForSite(siteId: string | null | undefined): StoredPoint | undefined {
  return siteId ? collectionPoints.find((p) => p.collection_site_id === siteId) : undefined;
}

export function createPoint(input: Record<string, unknown>): StoredPoint {
  const n = pointCounter.next();
  const point: StoredPoint = {
    id: ids.collectionPoint(n),
    supplier_id: String(input.supplier_id ?? ''),
    owner_org_id: R,
    name: String(input.name ?? 'New collection point'),
    address: (input.address as string | null | undefined) ?? null,
    city: (input.city as string | null | undefined) ?? 'Riyadh',
    latitude: Number(input.latitude ?? 24.7136),
    longitude: Number(input.longitude ?? 46.6753),
    google_maps_url: (input.google_maps_url as string | null | undefined) ?? null,
    status: (input.status as StoredPoint['status']) ?? 'active',
    contact_name: String(input.contact_name ?? ''),
    contact_phone: (input.contact_phone as string | null | undefined) ?? null,
    contact_email: (input.contact_email as string | null | undefined) ?? null,
    material_ids: Array.isArray(input.material_ids) ? (input.material_ids as string[]) : [],
    operating_hours: (input.operating_hours as StoredPoint['operating_hours']) ?? null,
    notes: (input.notes as string | null | undefined) ?? null,
    collection_site_id: (input.collection_site_id as string | null | undefined) ?? null,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  if (!point.google_maps_url) point.google_maps_url = mapsUrl(point.latitude, point.longitude);
  collectionPoints.push(point);
  return point;
}

export function updatePoint(id: string, patch: Record<string, unknown>): StoredPoint | undefined {
  const p = pointRecordById(id);
  if (!p) return undefined;
  const keys = ['address', 'latitude', 'longitude', 'google_maps_url', 'status', 'contact_name', 'contact_phone', 'contact_email', 'material_ids', 'operating_hours', 'notes'] as const;
  for (const key of keys) {
    if (patch[key] !== undefined) (p as unknown as Record<string, unknown>)[key] = patch[key];
  }
  if (typeof patch.name === 'string') {
    p.name = patch.name;
    p.name_ar = patch.name;
  }
  p.updated_at = nowIso();
  return p;
}

export function archivePoint(id: string): boolean {
  const p = pointRecordById(id);
  if (!p) return false;
  p.status = 'archived';
  p.updated_at = nowIso();
  return true;
}

// ── Facilities ─────────────────────────────────────────────────────────────

type StoredFacility = Facility & { name_ar?: string };
let facilities: StoredFacility[] = [];
const facilityCounter = makeCounter(100);

function facilityRecord(i: number): StoredFacility {
  const f = DEMO_FACILITIES[i];
  return {
    id: f.id,
    owner_org_id: R,
    name: f.name_en,
    name_ar: f.name_ar,
    type: (f.facility_type === 'sorting_plant' ? 'recycler_facility' : 'warehouse') as FacilityType,
    address: f.address,
    city: f.city,
    latitude: f.lat,
    longitude: f.lng,
    google_maps_url: mapsUrl(f.lat, f.lng),
    manager_user_id: f.manager_id,
    manager: null,
    status: 'active',
    source: 'created',
    linked_facility_id: null,
    linked_owner_org: null,
    created_by_user_id: DEMO_USER_ID,
    created_at: iso(daysAgo(600 - i * 120)),
    updated_at: iso(daysAgo(20 + i)),
  };
}

export function serializeFacility(f: StoredFacility): Facility {
  const manager = DEMO_RECYCLER_MANAGERS.find((m) => m.id === f.manager_user_id);
  const { name_ar: _nameAr, ...rest } = f;
  void _nameAr;
  return {
    ...rest,
    name: tr(f.name, f.name_ar),
    manager: f.manager_user_id
      ? { id: f.manager_user_id, username: manager ? manager.email.split('@')[0] : null, full_name: manager ? tr(manager.full_name, manager.full_name_ar) : null }
      : null,
  };
}

export function listFacilityRecords(): StoredFacility[] {
  return facilities.filter((f) => f.status !== 'archived');
}

export function facilityRecordById(id: string | null | undefined): StoredFacility | undefined {
  return facilities.find((f) => f.id === id);
}

export function facilityDisplayName(id: string | null | undefined): string | null {
  const f = facilityRecordById(id);
  return f ? tr(f.name, f.name_ar) : null;
}

export function createFacility(input: Record<string, unknown>): StoredFacility {
  const n = facilityCounter.next();
  const lat = (input.latitude as number | null | undefined) ?? null;
  const lng = (input.longitude as number | null | undefined) ?? null;
  const facility: StoredFacility = {
    id: ids.facility(n),
    owner_org_id: R,
    name: String(input.name ?? 'New facility'),
    type: (input.type as FacilityType) ?? 'recycler_facility',
    address: (input.address as string | null | undefined) ?? null,
    city: (input.city as string | null | undefined) ?? null,
    latitude: lat,
    longitude: lng,
    google_maps_url: (input.google_maps_url as string | null | undefined) ?? (lat != null && lng != null ? mapsUrl(lat, lng) : null),
    manager_user_id: (input.manager_user_id as string | null | undefined) ?? null,
    manager: null,
    status: (input.status as Facility['status']) ?? 'active',
    source: 'created',
    linked_facility_id: null,
    linked_owner_org: null,
    created_by_user_id: DEMO_USER_ID,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  facilities.push(facility);
  return facility;
}

export function updateFacility(id: string, patch: Record<string, unknown>): StoredFacility | undefined {
  const f = facilityRecordById(id);
  if (!f) return undefined;
  const keys = ['type', 'address', 'city', 'latitude', 'longitude', 'google_maps_url', 'manager_user_id', 'status'] as const;
  for (const key of keys) {
    if (patch[key] !== undefined) (f as unknown as Record<string, unknown>)[key] = patch[key];
  }
  if (typeof patch.name === 'string') {
    f.name = patch.name;
    f.name_ar = patch.name;
  }
  f.updated_at = nowIso();
  return f;
}

export function archiveFacility(id: string): boolean {
  const f = facilityRecordById(id);
  if (!f) return false;
  f.status = 'archived';
  f.updated_at = nowIso();
  return true;
}

// ── Routes ─────────────────────────────────────────────────────────────────

type StoredRoute = {
  id: string;
  name: string;
  name_ar?: string;
  city: string;
  status: Route['status'];
  stops: RouteStop[];
  assigned_truck_id: string | null;
  start_facility_id: string | null;
  end_facility_id: string | null;
  estimated_distance_km: number;
  created_at: string;
  updated_at: string;
};

let routes: StoredRoute[] = [];
const routeCounter = makeCounter(100);

function routeRecord(i: number): StoredRoute {
  const r = DEMO_ROUTES[i];
  const facility = DEMO_FACILITIES.find((f) => f.id === r.facility_id)!;
  return {
    id: r.id,
    name: r.name_en,
    name_ar: r.name_ar,
    city: facility.city,
    status: 'active',
    stops: r.stops.map((s) => ({ collection_point_id: s.collection_point_id, material_id: null })),
    assigned_truck_id: r.truck_id,
    start_facility_id: r.facility_id,
    end_facility_id: r.facility_id,
    estimated_distance_km: r.distance_km,
    created_at: iso(daysAgo(300 - i * 30)),
    updated_at: iso(daysAgo(12 + i * 2)),
  };
}

function facilitySummary(id: string | null) {
  const f = facilityRecordById(id);
  return f ? { id: f.id, name: tr(f.name, f.name_ar), type: f.type } : null;
}

function routeDistanceKm(route: StoredRoute): number {
  const start = facilityRecordById(route.start_facility_id);
  const end = facilityRecordById(route.end_facility_id);
  const coords: Array<[number, number]> = [];
  if (start?.longitude != null && start.latitude != null) coords.push([start.longitude, start.latitude]);
  route.stops.forEach((s) => {
    const p = pointRecordById(s.collection_point_id);
    if (p) coords.push([p.longitude, p.latitude]);
  });
  if (end?.longitude != null && end.latitude != null) coords.push([end.longitude, end.latitude]);
  return Math.round(polylineKm(coords) * 1.3 * 10) / 10;
}

export function serializeRoute(route: StoredRoute): Route {
  const truck = truckRecordById(route.assigned_truck_id);
  const pointIds = route.stops.map((s) => s.collection_point_id);
  const materials = new Set<string>();
  const labels = new Map<string, ZoneSummary>();
  route.stops.forEach((s) => {
    const p = pointRecordById(s.collection_point_id);
    if (!p) return;
    (s.material_id ? [s.material_id] : p.material_ids).forEach((m) => materials.add(m));
    labelsFor(p.latitude, p.longitude).forEach((l) => labels.set(l.id, l));
  });
  return {
    id: route.id,
    owner_org_id: R,
    name: tr(route.name, route.name_ar),
    city: route.city,
    status: route.status,
    collection_point_ids: Array.from(new Set(pointIds)),
    stops: route.stops.map((s) => ({ ...s })),
    stop_count: route.stops.length,
    material_ids: Array.from(materials),
    estimated_distance_km: route.estimated_distance_km,
    labels: Array.from(labels.values()),
    assigned_truck_id: route.assigned_truck_id,
    assigned_truck: truck ? { id: truck.id, name: truck.name, status: truck.status, capacity: truck.capacity } : null,
    start_facility_id: route.start_facility_id,
    start_facility: facilitySummary(route.start_facility_id),
    end_facility_id: route.end_facility_id,
    end_facility: facilitySummary(route.end_facility_id),
    created_by_user_id: DEMO_USER_ID,
    created_at: route.created_at,
    updated_at: route.updated_at,
  };
}

export function listRouteRecords(): StoredRoute[] {
  return routes.filter((r) => r.status !== 'archived');
}

export function routeRecordById(id: string | null | undefined): StoredRoute | undefined {
  return routes.find((r) => r.id === id);
}

export function routeDisplayName(id: string | null | undefined): string | null {
  const r = routeRecordById(id);
  return r ? tr(r.name, r.name_ar) : null;
}

function stopsFromInput(input: Record<string, unknown>): RouteStop[] | undefined {
  if (Array.isArray(input.stops) && input.stops.length) {
    return (input.stops as RouteStop[]).map((s) => ({ collection_point_id: s.collection_point_id, material_id: s.material_id ?? null }));
  }
  if (Array.isArray(input.collection_point_ids)) {
    return (input.collection_point_ids as string[]).map((id) => ({ collection_point_id: id, material_id: null }));
  }
  return undefined;
}

export function createRoute(input: Record<string, unknown>): StoredRoute {
  const n = routeCounter.next();
  const route: StoredRoute = {
    id: ids.route(n),
    name: String(input.name ?? 'New route'),
    city: String(input.city ?? 'Riyadh'),
    status: (input.status as Route['status']) ?? 'active',
    stops: stopsFromInput(input) ?? [],
    assigned_truck_id: null,
    start_facility_id: (input.start_facility_id as string | undefined) ?? DEMO_FACILITIES[0].id,
    end_facility_id: (input.end_facility_id as string | undefined) ?? DEMO_FACILITIES[0].id,
    estimated_distance_km: 0,
    created_at: nowIso(),
    updated_at: nowIso(),
  };
  route.estimated_distance_km = routeDistanceKm(route);
  routes.push(route);
  return route;
}

export function updateRoute(id: string, patch: Record<string, unknown>): StoredRoute | undefined {
  const route = routeRecordById(id);
  if (!route) return undefined;
  if (typeof patch.name === 'string') {
    route.name = patch.name;
    route.name_ar = patch.name;
  }
  if (typeof patch.city === 'string') route.city = patch.city;
  if (typeof patch.status === 'string') route.status = patch.status as Route['status'];
  if (typeof patch.start_facility_id === 'string') route.start_facility_id = patch.start_facility_id;
  if (typeof patch.end_facility_id === 'string') route.end_facility_id = patch.end_facility_id;
  const stops = stopsFromInput(patch);
  if (stops) route.stops = stops;
  route.estimated_distance_km = routeDistanceKm(route);
  route.updated_at = nowIso();
  return route;
}

export function archiveRoute(id: string): boolean {
  const route = routeRecordById(id);
  if (!route) return false;
  route.status = 'archived';
  route.updated_at = nowIso();
  return true;
}

export function setRouteTruck(id: string, truckId: string | null): StoredRoute | undefined {
  const route = routeRecordById(id);
  if (!route) return undefined;
  route.assigned_truck_id = truckId;
  route.updated_at = nowIso();
  return route;
}

// ── Supplier agreements ────────────────────────────────────────────────────

let agreementPrices: SupplierAgreementPrice[] = [];
let agreementDocuments: SupplierAgreementDocument[] = [];
const priceCounter = makeCounter(500);
const documentCounter = makeCounter(100);

function priceString(n: number): string {
  return round2(n).toFixed(2);
}

function seedAgreements(): void {
  agreementPrices = [];
  let n = 0;
  DEMO_SUPPLIERS.forEach((supplier, i) => {
    SUPPLIER_MATERIALS[i].forEach((materialId) => {
      const base = MATERIAL_PRICE_SAR_PER_KG[materialId] ?? 0.2;
      const active = i === 0 ? base : round2(base * PRICE_FACTOR[i]);
      if (i > 0) {
        n += 1;
        agreementPrices.push({
          id: ids.agreementPrice(n),
          supplier_id: supplier.id,
          material_id: materialId,
          price: priceString(active * 0.94),
          status: 'superseded',
          effective_date: isoDate(daysAgo(330)),
          superseded_at: iso(daysAgo(90)),
          created_by_user_id: DEMO_USER_ID,
          created_at: iso(daysAgo(332)),
          updated_at: iso(daysAgo(90)),
        });
      }
      n += 1;
      agreementPrices.push({
        id: ids.agreementPrice(n),
        supplier_id: supplier.id,
        material_id: materialId,
        price: i === 0 ? String(base) : priceString(active),
        status: 'active',
        effective_date: isoDate(daysAgo(i === 0 ? 400 : 90)),
        superseded_at: null,
        created_by_user_id: DEMO_USER_ID,
        created_at: iso(daysAgo(i === 0 ? 401 : 91)),
        updated_at: iso(daysAgo(i === 0 ? 401 : 91)),
      });
    });
  });
  const docRows: Array<[number, string, string, number]> = [
    [0, 'Al-Nakheel-master-supply-agreement-2026.pdf', 'Master supply agreement', 400],
    [0, 'Al-Nakheel-price-schedule.pdf', 'Price schedule', 120],
    [1, 'Faisaliah-Hotel-collection-agreement.pdf', 'Collection agreement', 92],
    [2, 'KFMC-non-clinical-recyclables-contract.pdf', 'Service contract', 95],
    [3, 'Granada-Mall-service-yard-access.pdf', 'Site access letter', 88],
    [4, 'Riyadh-Schools-recycling-programme.pdf', 'Programme agreement', 91],
  ];
  agreementDocuments = docRows.map(([supplierIdx, filename, label, days], i) => ({
    id: ids.document(i + 1),
    supplier_id: DEMO_SUPPLIERS[supplierIdx].id,
    filename,
    doc_label: label,
    asset_id: ids.upload(0x9000 + i),
    file_url: null,
    created_at: iso(daysAgo(days)),
  }));
}

export function listAgreementPrices(supplierId: string): SupplierAgreementPrice[] {
  return agreementPrices
    .filter((p) => p.supplier_id === supplierId)
    .sort((a, b) => (a.status === b.status ? b.effective_date.localeCompare(a.effective_date) : a.status === 'active' ? -1 : 1));
}

/** POST …/agreement/prices: the new row supersedes the active one for that material. */
export function setAgreementPrice(supplierId: string, input: Record<string, unknown>): SupplierAgreementPrice {
  const materialId = String(input.material_id ?? '');
  const now = nowIso();
  agreementPrices.forEach((p) => {
    if (p.supplier_id === supplierId && p.material_id === materialId && p.status === 'active') {
      p.status = 'superseded';
      p.superseded_at = now;
      p.updated_at = now;
    }
  });
  const row: SupplierAgreementPrice = {
    id: ids.agreementPrice(priceCounter.next()),
    supplier_id: supplierId,
    material_id: materialId,
    price: priceString(Number(input.price ?? 0)),
    status: 'active',
    effective_date: String(input.effective_date ?? now.slice(0, 10)).slice(0, 10),
    superseded_at: null,
    created_by_user_id: DEMO_USER_ID,
    created_at: now,
    updated_at: now,
  };
  agreementPrices.push(row);
  return row;
}

/** Agreement price (SAR/kg) for a supplier's material on a date; null when none applies. */
export function agreementPriceOn(supplierId: string | null | undefined, materialId: string, when: string): number | null {
  if (!supplierId) return null;
  const day = when.slice(0, 10);
  const rows = agreementPrices
    .filter((p) => p.supplier_id === supplierId && p.material_id === materialId && p.effective_date <= day)
    .sort((a, b) => b.effective_date.localeCompare(a.effective_date) || b.created_at.localeCompare(a.created_at));
  return rows.length ? Number(rows[0].price) : null;
}

export function listAgreementDocuments(supplierId: string): SupplierAgreementDocument[] {
  const supplier = supplierRecordById(supplierId);
  return agreementDocuments
    .filter((d) => d.supplier_id === supplierId)
    .map((d) => ({
      ...d,
      file_url:
        uploadedFileUrl(d.asset_id) ??
        generatedPdfUrl(d.id, d.doc_label ?? d.filename, [
          `Supplier: ${supplier?.name ?? ''}`,
          `Buyer: Riyadh Green Recycling Co.`,
          `Document: ${d.filename}`,
          `Signed: ${d.created_at.slice(0, 10)}`,
          ...listAgreementPrices(supplierId)
            .filter((p) => p.status === 'active')
            .map((p) => `${materialById(p.material_id)?.name_en ?? p.material_id}: SAR ${p.price} per kg from ${p.effective_date}`),
        ]),
    }))
    .sort((a, b) => b.created_at.localeCompare(a.created_at));
}

export function attachAgreementDocument(supplierId: string, input: Record<string, unknown>): SupplierAgreementDocument {
  const doc: SupplierAgreementDocument = {
    id: ids.document(documentCounter.next()),
    supplier_id: supplierId,
    filename: String(input.filename ?? 'document.pdf'),
    doc_label: (input.doc_label as string | null | undefined) ?? null,
    asset_id: String(input.asset_id ?? ''),
    file_url: uploadedFileUrl(String(input.asset_id ?? '')),
    created_at: nowIso(),
  };
  agreementDocuments.push(doc);
  return doc;
}

export function archiveAgreementDocument(supplierId: string, documentId: string): boolean {
  const before = agreementDocuments.length;
  agreementDocuments = agreementDocuments.filter((d) => !(d.supplier_id === supplierId && d.id === documentId));
  return agreementDocuments.length !== before;
}

// ── Completion flows ───────────────────────────────────────────────────────

type StepRow = [key: string, kind: FlowStepType['kind'], semantic: string | null, en: string, ar: string, help: string, appliesTo: FlowType[], config: Record<string, unknown>];

const STEP_ROWS: StepRow[] = [
  ['arrival_photo', 'single_photo', 'arrival_evidence', 'Arrival photo', 'صورة الوصول', 'Photo of the staging area on arrival.', ['supplier_collection'], {}],
  ['fill_level', 'choice', 'fill_level', 'Fill level', 'مستوى الامتلاء', 'How full the bins were.', ['supplier_collection'], { options: ['FULL', 'THREE_QT_FULL', 'HALF_FULL', 'QT_FULL'] }],
  ['entered_quantity', 'number', 'entered_quantity', 'Estimated quantity (kg)', 'الكمية التقديرية (كجم)', 'Driver estimate before weighing.', ['supplier_collection'], { unit: 'kg', min: 0 }],
  ['loading_photos', 'photo_set', 'loading_evidence', 'Loading photos', 'صور التحميل', 'Before and after loading.', ['supplier_collection'], { min: 2, max: 6 }],
  ['contamination_check', 'boolean', 'contamination', 'Contamination found?', 'هل وجد تلوث؟', 'Flag food or liquid contamination.', ['supplier_collection'], {}],
  ['point_signature', 'signature', 'pickup_approval', 'Point contact signature', 'توقيع مسؤول النقطة', 'The contact confirms the load.', ['supplier_collection'], {}],
  ['safety_ack', 'acknowledge', null, 'PPE and safety briefing', 'معدات الوقاية والسلامة', 'Confirm PPE is worn.', ['supplier_collection', 'facility_offload'], {}],
  ['driver_notes', 'text', null, 'Notes', 'ملاحظات', 'Anything the office should know.', ['supplier_collection', 'facility_offload'], { max_length: 500 }],
  ['gate_photo', 'single_photo', 'gate_evidence', 'Gate check-in photo', 'صورة الدخول من البوابة', 'Photo at the facility gate.', ['facility_offload'], {}],
  ['weighbridge_ticket', 'single_photo', 'weight_invoice', 'Weighbridge ticket', 'تذكرة الميزان', 'Photo of the weighbridge ticket.', ['facility_offload'], {}],
  ['net_weight', 'number', 'net_weight', 'Net weight (kg)', 'الوزن الصافي (كجم)', 'Gross minus tare from the ticket.', ['facility_offload'], { unit: 'kg', min: 0 }],
  ['offload_photos', 'photo_set', 'offload_evidence', 'Offload photos', 'صور التفريغ', 'The load on the tipping floor.', ['facility_offload'], { min: 1, max: 4 }],
];

export const FLOW_STEP_TYPES: FlowStepType[] = STEP_ROWS.map(([key, kind, semantic, en, ar, help, appliesTo, config], i) => ({
  id: demoIdFor(0xe5, i + 1),
  key,
  kind,
  semantic,
  labels: { en, ar },
  help_text: { en: help },
  default_config: config,
  applies_to: appliesTo,
  is_system: true,
  status: 'active',
}));

function demoIdFor(code: number, n: number): string {
  return `00000000-0000-4000-8000-0000${code.toString(16)}${n.toString(16).padStart(6, '0')}`;
}

export const FLOW_CONTRACTS: Record<FlowType, FlowContract> = {
  supplier_collection: {
    flow_type: 'supplier_collection',
    required_semantics: ['loading_evidence'],
    any_of_semantics: [['fill_level', 'entered_quantity']],
    invoice_required: false,
  },
  facility_offload: {
    flow_type: 'facility_offload',
    required_semantics: ['weight_invoice', 'net_weight'],
    any_of_semantics: [],
    invoice_required: true,
  },
};

const DEFAULT_STEPS: Record<FlowType, Array<[string, string, boolean]>> = {
  supplier_collection: [
    ['arrival_photo', 'arrival', false],
    ['safety_ack', 'arrival', true],
    ['fill_level', 'loading', true],
    ['loading_photos', 'loading', true],
    ['point_signature', 'loading', false],
  ],
  facility_offload: [
    ['gate_photo', 'at_facility', false],
    ['weighbridge_ticket', 'weighing', true],
    ['net_weight', 'weighing', true],
    ['offload_photos', 'at_facility', false],
  ],
};

let flows: CollectionFlow[] = [];
const flowCounter = makeCounter(100);

function stepType(key: string): FlowStepType | undefined {
  return FLOW_STEP_TYPES.find((t) => t.key === key);
}

function toFlowSteps(inputs: FlowStepInput[]): CollectionFlowStep[] {
  return inputs.map((s, i) => ({
    ...s,
    step_type_key: s.step_type_key ?? s.step_key,
    id: demoIdFor(0xe6, flowCounter.next() * 16 + i),
    step_order: i + 1,
    flow_step_type_id: stepType(s.step_type_key ?? s.step_key)?.id ?? '',
  }));
}

function defaultInputs(flowType: FlowType): FlowStepInput[] {
  return DEFAULT_STEPS[flowType].map(([key, stage, required]) => ({
    step_key: key,
    step_type_key: key,
    stage: stage as FlowStepInput['stage'],
    is_required: required,
    config: { ...(stepType(key)?.default_config ?? {}) },
  }));
}

function seedFlows(): void {
  flows = [];
  // The outlet supplier has its own signed-handshake collection flow; the Riyadh plant its own offload flow.
  flows.push({
    id: demoIdFor(0xe7, 1),
    owner_org_id: R,
    flow_type: 'supplier_collection',
    supplier_id: DEMO_SUPPLIERS[0].id,
    facility_id: null,
    version: 2,
    status: 'active',
    name: 'Al Nakheel collection',
    steps: toFlowSteps(defaultInputs('supplier_collection').map((s) => (s.step_key === 'point_signature' ? { ...s, is_required: true } : s))),
  });
  flows.push({
    id: demoIdFor(0xe7, 2),
    owner_org_id: R,
    flow_type: 'facility_offload',
    supplier_id: null,
    facility_id: DEMO_FACILITIES[0].id,
    version: 1,
    status: 'active',
    name: 'Sulay plant offload',
    steps: toFlowSteps(defaultInputs('facility_offload')),
  });
}

export function listFlows(query: { supplier_id?: string; facility_id?: string; include_superseded?: boolean }): CollectionFlow[] {
  return flows.filter(
    (f) =>
      (!query.supplier_id || f.supplier_id === query.supplier_id) &&
      (!query.facility_id || f.facility_id === query.facility_id) &&
      (query.include_superseded || f.status !== 'superseded'),
  );
}

export function resolveFlow(flowType: FlowType, subject: { supplier_id?: string; facility_id?: string }): ResolvedFlow {
  const active = flows.find(
    (f) =>
      f.flow_type === flowType &&
      f.status === 'active' &&
      ((subject.supplier_id && f.supplier_id === subject.supplier_id) || (subject.facility_id && f.facility_id === subject.facility_id)),
  );
  const steps = active ? active.steps : toFlowSteps(defaultInputs(flowType));
  return {
    flow_id: active?.id ?? null,
    version: active?.version ?? null,
    flow_type: flowType,
    is_default: !active,
    steps: steps.map((s) => {
      const type = stepType(s.step_type_key ?? s.step_key);
      return {
        step_key: s.step_key,
        step_type_key: s.step_type_key ?? s.step_key,
        kind: type?.kind ?? 'text',
        semantic: type?.semantic ?? null,
        stage: s.stage,
        required: s.is_required,
        config: s.config,
        labels: type?.labels ?? { en: s.step_key },
      };
    }),
  };
}

export function saveFlowDraft(body: Record<string, unknown>): CollectionFlow {
  const flowType = (body.flow_type as FlowType) ?? 'supplier_collection';
  const supplierId = (body.supplier_id as string | undefined) ?? null;
  const facilityId = (body.facility_id as string | undefined) ?? null;
  const steps = Array.isArray(body.steps) ? (body.steps as FlowStepInput[]) : [];
  let draft = flows.find((f) => f.status === 'draft' && f.flow_type === flowType && f.supplier_id === supplierId && f.facility_id === facilityId);
  const latest = Math.max(0, ...flows.filter((f) => f.flow_type === flowType && f.supplier_id === supplierId && f.facility_id === facilityId).map((f) => f.version));
  if (!draft) {
    draft = {
      id: demoIdFor(0xe7, flowCounter.next()),
      owner_org_id: R,
      flow_type: flowType,
      supplier_id: supplierId,
      facility_id: facilityId,
      version: latest + 1,
      status: 'draft',
      name: (body.name as string | undefined) ?? null,
      steps: [],
    };
    flows.push(draft);
  }
  draft.steps = toFlowSteps(steps);
  return draft;
}

/** Contract-checked activation; returns the missing semantics when it cannot activate. */
export function activateFlow(flowId: string): CollectionFlow | { missing: string[] } | null {
  const flow = flows.find((f) => f.id === flowId);
  if (!flow) return null;
  const contract = FLOW_CONTRACTS[flow.flow_type];
  const requiredSemantics = flow.steps
    .filter((s) => s.is_required)
    .map((s) => stepType(s.step_type_key ?? s.step_key)?.semantic)
    .filter((s): s is string => Boolean(s));
  const missing = contract.required_semantics.filter((s) => !requiredSemantics.includes(s));
  (contract.any_of_semantics ?? []).forEach((group) => {
    if (!group.some((s) => requiredSemantics.includes(s))) missing.push(group.join(' / '));
  });
  if (missing.length) return { missing };
  flows.forEach((f) => {
    if (f.id !== flow.id && f.status === 'active' && f.flow_type === flow.flow_type && f.supplier_id === flow.supplier_id && f.facility_id === flow.facility_id) {
      f.status = 'superseded';
    }
  });
  flow.status = 'active';
  return flow;
}

// ── Reset ──────────────────────────────────────────────────────────────────

export function resetNetwork(): void {
  supplierCounter.reset();
  zoneCounter.reset();
  pointCounter.reset();
  facilityCounter.reset();
  routeCounter.reset();
  priceCounter.reset();
  documentCounter.reset();
  flowCounter.reset();
  zoneNamesAr.clear();
  suppliers = DEMO_SUPPLIERS.map((_, i) => supplierRecord(i));
  zones = DEMO_ZONES.map((z, i) => {
    zoneNamesAr.set(z.id, z.name_ar);
    return {
      id: z.id,
      owner_org_id: R,
      name: z.name_en,
      color: z.color,
      boundary: z.polygon.map(([lng, lat]) => [lng, lat]),
      status: 'active',
      collection_points_count: 0,
      created_by_user_id: DEMO_USER_ID,
      created_at: iso(daysAgo(320 - i * 15)),
      updated_at: iso(daysAgo(30 + i)),
    } as Zone;
  });
  collectionPoints = DEMO_COLLECTION_POINTS.map((_, i) => pointRecord(i));
  facilities = DEMO_FACILITIES.map((_, i) => facilityRecord(i));
  routes = DEMO_ROUTES.map((_, i) => routeRecord(i));
  seedAgreements();
  seedFlows();
}

/** POST /ops/suppliers/{id}/sync-collection-sites: mirror outlet sites not yet present as points. */
export function syncSupplierSites(
  supplierId: string,
  outletSites: Array<{ id: string; name: string; latitude?: number; longitude?: number; address?: string | null; city?: string | null; contact?: { name?: string; phone?: string; email?: string }; materialIds: string[] }>,
): { created: SupplierCollectionPoint[]; skipped: Array<{ collection_site_id: string; reason: string }> } {
  const supplier = supplierRecordById(supplierId);
  const created: SupplierCollectionPoint[] = [];
  const skipped: Array<{ collection_site_id: string; reason: string }> = [];
  if (!supplier || supplier.supplier_org_id !== DEMO_OUTLET_ORG_ID) return { created, skipped };
  outletSites.forEach((site) => {
    if (pointForSite(site.id)) {
      skipped.push({ collection_site_id: site.id, reason: 'already_synced' });
      return;
    }
    if (site.latitude == null || site.longitude == null) {
      skipped.push({ collection_site_id: site.id, reason: 'missing_coordinates' });
      return;
    }
    const point = createPoint({
      supplier_id: supplierId,
      name: `${supplier.name} - ${site.name}`,
      address: site.address ?? null,
      city: site.city ?? 'Riyadh',
      latitude: site.latitude,
      longitude: site.longitude,
      contact_name: site.contact?.name ?? '',
      contact_phone: site.contact?.phone ?? null,
      contact_email: site.contact?.email ?? null,
      material_ids: site.materialIds,
      collection_site_id: site.id,
      notes: `Synced from ${supplier.name}.`,
    });
    created.push(serializePoint(point));
  });
  return { created, skipped };
}
